import React, { useCallback, useEffect, useRef } from 'react'
import { Box, Button, IconButton, Tooltip, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Close as CloseIcon, InfoOutlined as InfoIcon } from '@material-ui/icons'
import { useSnackbar } from 'notistack5'
import FileNameText from 'src/components/FileNameText'
import { KindIcon } from 'src/views/Batches/BatchParts'
import ResultButton from 'src/views/FileUploadView/components/ResultButton'
import HomeCard, { CardMessage, RowSkeletons, listSx, metaSx, rowSx } from './HomeCard'
import { DISMISS_DAYS, suggestionCountText, usageText } from './suggestions'
import useSuggestions from './useSuggestions'

// ----------------------------------------------------------------------
// Home's "Suggested" card: setups the user ran again and again lately, each ready to
// open in New analysis with the waiting samples it fits and a batch name in the
// style of the past ones. It never starts anything.
//
//   WES run 42                                       ×  [Review]
//   [dna] Whole exome · Twist Exome v2 · CNV
//   60 waiting samples · used 38× in 30 days  (i)
//
// "Review" calls onReview(suggestion) (samples, workflow and batch name); a setup
// with no waiting sample shows "Use setup", onUseSetup(suggestion) (the workflow
// only). "Not now" (×, on hover or focus) hides the setup for DISMISS_DAYS days, per
// user in this browser, with an undo.
// ----------------------------------------------------------------------

// Name, hide, action on the first line; the workflow and usage lines below span the
// whole row, so they are not cut short where the card is a third of the page. Hide
// sits before the action, so the buttons line up with the card's right edge as in
// Ready to analyse.
const COLUMNS = 'minmax(0, 1fr) auto auto'
const HIDE_BUTTON_SIZE = 26

const lineSx = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const SuggestionRow = ({ suggestion, onOpen, onHide, actionRef }) => {
  const theme = useTheme()
  const setupOnly = suggestion.setupOnly
  const actionLabel = setupOnly ? 'Use setup' : 'Review'
  return (
    <Box
      data-row
      data-testid="suggestion"
      sx={{
        ...rowSx(theme),
        alignItems: 'center',
        rowGap: 0.25,
        py: 1.5,
        '& .suggestion-hide': { opacity: 0, transition: theme.transitions.create('opacity', { duration: 120 }) },
        '&:hover .suggestion-hide, &:focus-within .suggestion-hide': { opacity: 1 },
        '@media (hover: none)': { '& .suggestion-hide': { opacity: 1 } },
      }}
    >
      <Box sx={{ minWidth: 0, gridRow: 1, gridColumn: 1 }}>
        {suggestion.titleIsSample ? (
          <FileNameText
            name={suggestion.title}
            variant="plain"
            maxLines={1}
            sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.45 }}
          />
        ) : (
          <Box sx={{ ...lineSx, fontSize: 15, fontWeight: 600, lineHeight: 1.4 }}>{suggestion.title}</Box>
        )}
      </Box>
      <Box sx={{ minWidth: 0, gridRow: 2, gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
          <Box
            role="img"
            aria-label={suggestion.kind === 'somatic' ? 'Somatic' : 'Germline'}
            sx={{ display: 'inline-flex', height: 18, alignItems: 'center', flexShrink: 0 }}
          >
            <KindIcon kind={suggestion.kind} size={14} />
          </Box>
          <Typography
            sx={{
              fontSize: 13,
              lineHeight: 1.4,
              color: 'text.secondary',
              minWidth: 0,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflowWrap: 'anywhere',
            }}
          >
            {suggestion.shortSummary}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography sx={{ ...metaSx, fontSize: 12.5, whiteSpace: 'normal' }}>{usageText(suggestion)}</Typography>
          <Tooltip title={suggestion.reason} describeChild>
            <IconButton
              aria-label="Why this suggestion"
              sx={{
                width: 20,
                height: 20,
                p: 0,
                flexShrink: 0,
                color: 'text.secondary',
                borderRadius: `${theme.shape.borderRadius}px`,
                '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
              }}
            >
              <InfoIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Tooltip title="Not now" describeChild>
        <IconButton
          className="suggestion-hide"
          aria-label={`Not now: ${suggestion.title}`}
          // A click from the keyboard (Enter, Space) has no pointer detail.
          onClick={(event) => onHide(suggestion, event.detail === 0)}
          sx={{
            gridRow: 1,
            gridColumn: 2,
            width: HIDE_BUTTON_SIZE,
            height: HIDE_BUTTON_SIZE,
            p: 0,
            borderRadius: `${theme.shape.borderRadius}px`,
            color: 'text.secondary',
            '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
            '&.Mui-focusVisible': { opacity: 1, outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Box sx={{ gridRow: 1, gridColumn: 3, justifySelf: 'end' }}>
        <ResultButton
          ref={actionRef}
          compact
          aria-label={`${actionLabel}: ${suggestion.title}`}
          onClick={() => onOpen(suggestion)}
          onKeyDown={(event) => {
            if (event.key === 'Delete' || event.key === 'Backspace') {
              event.preventDefault()
              onHide(suggestion, true)
            }
          }}
        >
          {actionLabel}
        </ResultButton>
      </Box>
    </Box>
  )
}

/**
 * ready: Home's useReadySamples result, shared with Ready to analyse.
 * onReview(suggestion) / onUseSetup(suggestion): open New analysis.
 */
const SuggestedCard = ({ ready, onReview, onUseSetup }) => {
  const theme = useTheme()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { suggestions, loaded, failed, retry, dismiss, undismiss, allDismissed } = useSuggestions(ready)

  const open = useCallback(
    (suggestion) => (suggestion.setupOnly ? onUseSetup(suggestion) : onReview(suggestion)),
    [onReview, onUseSetup],
  )

  // ------------------------------------------------------------ "Not now", with undo
  const actionRefs = useRef([])
  const focusAfterHide = useRef(null)

  const hide = useCallback(
    (suggestion, fromKeyboard) => {
      if (fromKeyboard) focusAfterHide.current = Math.max(0, suggestions.indexOf(suggestion))
      dismiss(suggestion.id)
      enqueueSnackbar(`Suggestion hidden for ${DISMISS_DAYS} days`, {
        autoHideDuration: 6000,
        action: (key) => (
          <Button
            size="small"
            color="inherit"
            onClick={() => {
              undismiss(suggestion.id)
              closeSnackbar(key)
            }}
            sx={{ fontWeight: 600, minWidth: 0 }}
          >
            Undo
          </Button>
        ),
      })
    },
    [suggestions, dismiss, undismiss, enqueueSnackbar, closeSnackbar],
  )

  // After a keyboard hide, focus moves to the action now in its place, or the one
  // before it, instead of the page.
  useEffect(() => {
    if (focusAfterHide.current === null) return
    const start = Math.min(focusAfterHide.current, suggestions.length - 1)
    focusAfterHide.current = null
    let target = null
    for (let index = start; index >= 0 && !target; index -= 1) target = actionRefs.current[index] || null
    if (target && typeof target.focus === 'function') target.focus()
  })

  let body
  if (failed) {
    body = (
      <CardMessage
        title="Could not load suggestions."
        action={
          <ResultButton compact onClick={() => retry()}>
            Try again
          </ResultButton>
        }
      />
    )
  } else if (!loaded) {
    body = <RowSkeletons count={2} />
  } else if (!suggestions.length) {
    body = allDismissed ? (
      <CardMessage title="No suggestions right now." detail={`Hidden ones come back after ${DISMISS_DAYS} days.`} />
    ) : (
      <CardMessage
        title="No suggestions yet."
        detail="Genesus suggests a setup here once you have run it a few times."
      />
    )
  } else {
    actionRefs.current = []
    body = (
      <Box sx={{ ...listSx(theme, COLUMNS), pb: 1 }}>
        {suggestions.map((suggestion, index) => (
          <SuggestionRow
            key={suggestion.id}
            suggestion={suggestion}
            onOpen={open}
            onHide={hide}
            actionRef={(el) => {
              actionRefs.current[index] = el
            }}
          />
        ))}
      </Box>
    )
  }

  return (
    <HomeCard
      title="Suggested"
      titleId="home-suggested-title"
      count={loaded && !failed ? suggestionCountText(suggestions.length) : ''}
      data-testid="suggested-card"
    >
      {body}
    </HomeCard>
  )
}

export default SuggestedCard
